import {
  Breakpoint,
  Card,
  CardGroupCard,
  CardVariant,
  CollapsibleSidePanel,
  Color,
  ContentFilter,
  ContentFilterOption,
  ContentFilterType,
  ContentTableItem,
  DatesRange,
  FullScreenAlertVariant,
  HelpBanner,
  HelpBannerType,
  useNotifications,
  UseNotifications,
  UseState,
} from '@elearning-platform/ui';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { Location, useLocation } from 'react-router';

import { downloadFile, getThumbnailListsAndTags } from '../../api/requests';
import { AppConfig } from '../../appConfig';
import { KnowledgeBaseViewMode, LessonStatus, QueryKey, RouteNameEnum, ThumbnailType, ViewImage } from '../../enums';
import { useAuth, useLessonCard, useRedirectTo, useScrollMemory } from '../../hooks';
import { Tag, Thumbnail, UseAuth, UseLessonCard, UseRedirectTo } from '../../interfaces';
import { FrontendPhpApiError } from '../../interfaces/api';
import { normalizeSearchValue } from '../../utils';

import { CategorySection, ViewOptions } from './components';
import { CategoryData } from './interfaces';
import { filterableAttributes, filters, skeletonCardItem, skeletonTableItem } from './knowledgeBaseV2.consts';
import { CategoryFilter, FilterName } from './knowledgeBaseV2.enums';
import {
  BottomContainer,
  CollapsibleSidePanelCardWrapper,
  Container,
  DownloadBoxWrapper,
  MiddleContainer,
  Separator,
  StyledContentFilters,
  StyledDownloadBox,
  StyledEmptyPlaceholder,
} from './knowledgeBaseV2.styled';
import { FilterableAttribute, KnowledgeBaseData, ThumbnailList } from './knowledgeBaseV2.types';

const KnowledgeBaseV2: React.FC = (): JSX.Element => {
  const { t }: TransProps<never> = useTranslation();
  const [searchValue, setSearchValue]: UseState<string> = useState<string>('');
  const [activeFilters, setActiveFilters]: UseState<Record<string, string[] | DatesRange>>
    = useState<Record<string, string[] | DatesRange>>({});
  const { hash }: Location = useLocation();
  const isSmallDesktop: boolean = useMediaQuery({ query: Breakpoint.SmallDesktop });
  const {
    getCardImage,
    itemToShowOnSidePanel,
    setItemToShowOnSidePanel,
    handleOnCardClick,
    transformThumbnailIntoCardData,
    setIsSidePanelOpen,
    isSidePanelOpen,
    transformThumbnailIntoTableData,
  }: UseLessonCard = useLessonCard();
  useScrollMemory(false);
  const { showFullscreenAlert, hideFullscreenAlert }: UseNotifications = useNotifications();
  const { redirect }: UseRedirectTo = useRedirectTo();
  const { appSettings }: UseAuth = useAuth();
  const viewMode: KnowledgeBaseViewMode
    = appSettings?.knowledgeBaseViewMode ?? KnowledgeBaseViewMode.Grid;

  const { data: lessons, isFetching, isPending, error, isError }: UseQueryResult<KnowledgeBaseData, FrontendPhpApiError> = useQuery({
    queryKey: [QueryKey.KnowledgeBaseData],
    queryFn: (): Promise<KnowledgeBaseData> => getThumbnailListsAndTags(),
  });

  useEffect((): void => {
    if (isError) {
      showFullscreenAlert({
        title: t('competence.app.knowledgeBaseV2.defaultErrorHeader'),
        description: error?.response?.data?.message || t('competence.app.knowledgeBaseV2.defaultErrorDescription'),
        variant: FullScreenAlertVariant.Dark,
        acceptButtonData: {
          label: t('competence.app.knowledgeBaseV2.mainPage'),
          onClick: (): void => {
            redirect({ route: RouteNameEnum.Dashboard });
            hideFullscreenAlert();
          },
        },
      });
    }
  }, [isError, error, redirect, showFullscreenAlert, hideFullscreenAlert, t]);

  useEffect(
    (): void => {
      const id: number = Number(hash.replace('#', ''));
      if (!lessons || isNaN(id) || itemToShowOnSidePanel) {
        return;
      }
      const content: Thumbnail | undefined = lessons?.elements
        .flatMap((item: ThumbnailList): Thumbnail[] => item.items)
        .find((item: Thumbnail): boolean => item.id === id && item.type === ThumbnailType.Contents);
      if (content) {
        setItemToShowOnSidePanel(content);
        setIsSidePanelOpen(true);
      }
    },
    [lessons, hash, itemToShowOnSidePanel, setIsSidePanelOpen, setItemToShowOnSidePanel]
  );

  const filterByType: (item: Thumbnail) => boolean = useCallback((item: Thumbnail): boolean => {
    const typesToFilter: string[] | DatesRange = activeFilters[FilterName.Type];
    if (typesToFilter && Array.isArray(typesToFilter)) {
      if (typesToFilter.includes('favorite')) {
        return !!item.favorite;
      } else if ([ThumbnailType.File, ThumbnailType.Files].includes(item.type)) {
        return typesToFilter.some((filter: string): boolean => filter === CategoryFilter.Files);
      } else if ([ThumbnailType.Lesson, ThumbnailType.LessonSlideScroll].includes(item.type)) {
        return typesToFilter.some((filter: string): boolean => filter === CategoryFilter.ELearning);
      } else if ([ThumbnailType.Items, ThumbnailType.Video].includes(item.type)) {
        return typesToFilter.some((filter: string): boolean => filter === CategoryFilter.Video);
      }

      return false;
    }

    return true;
  }, [activeFilters]);

  const filterByTags: (item: Thumbnail) => boolean = useCallback(
    (item: Thumbnail): boolean => {
      const tagsToFilter: string[] | DatesRange = activeFilters[FilterName.Tags];
      if (tagsToFilter && Array.isArray(tagsToFilter)) {
        return item.tags.some((tag: Tag): boolean => !!tagsToFilter.find((item: string): boolean => item === tag.name));
      }

      return true;
    },
    [activeFilters]
  );

  const filterByAttributes: (item: Thumbnail) => boolean = useCallback(
    (item: Thumbnail): boolean => {
      const attributesToFilter: string[] | DatesRange = activeFilters[FilterName.Attributes];
      if (attributesToFilter && Array.isArray(attributesToFilter)) {
        return filterableAttributes.some((attr: FilterableAttribute): boolean => !!item[attr.value]
          && attributesToFilter.includes(attr.value)
        );
      }

      return true;
    },
    [activeFilters]
  );

  const filterByStatus: (item: Thumbnail) => boolean = useCallback(
    (item: Thumbnail): boolean => {
      const statusesToFilter: string[] | DatesRange = activeFilters[FilterName.Status];
      if (statusesToFilter && Array.isArray(statusesToFilter)) {
        return !!item.statusKey && statusesToFilter.includes(item.statusKey)
          || (statusesToFilter.includes(LessonStatus.Finished) && item.statusKey === LessonStatus.ValidateTo);
      }

      return true;
    },
    [activeFilters]
  );

  const filterBySearchValue: (item: Thumbnail) => boolean = useCallback(
    (item: Thumbnail): boolean => !searchValue || normalizeSearchValue(item.name)
      .includes(normalizeSearchValue(searchValue)), [searchValue]);

  const filterThumbnails: (thumbnails: Thumbnail[]) => Thumbnail[] = useCallback((thumbnails: Thumbnail[]): Thumbnail[] => (
    thumbnails.filter((item: Thumbnail): boolean => (
      filterByStatus(item)
      && filterByType(item)
      && filterByAttributes(item)
      && filterByTags(item)
      && filterBySearchValue(item)
    ))
  ), [filterByTags, filterByType, filterByAttributes, filterByStatus, filterBySearchValue]);

  const inProgressMaterials: Thumbnail[] = useMemo((): Thumbnail[] => {
    const inProgress: Thumbnail[] = [];
    if (lessons) {
      lessons.elements.forEach((list: ThumbnailList): void => list.items.forEach((item: Thumbnail): void => {
        if (item.statusKey === LessonStatus.InProgress) {
          inProgress.push(item);
        }
      }));
    }

    return inProgress;
  }, [lessons]);

  const inProgressCardData: CardGroupCard[] = useMemo((): CardGroupCard[] => {
    if (isFetching || isPending) {
      return Array.from({ length: 6 }, (): CardGroupCard => skeletonCardItem);
    }

    return filterThumbnails(inProgressMaterials).map(transformThumbnailIntoCardData);
  }, [isFetching, isPending, inProgressMaterials, filterThumbnails, transformThumbnailIntoCardData]);

  const inProgressTableData: ContentTableItem[] = useMemo((): ContentTableItem[] => {
    if (isFetching || isPending) {
      return Array.from({ length: 4 }, (): ContentTableItem => skeletonTableItem);
    }

    return filterThumbnails(inProgressMaterials).map(transformThumbnailIntoTableData);
  }, [isFetching, isPending, inProgressMaterials, filterThumbnails, transformThumbnailIntoTableData]);

  const categoryData: CategoryData[] | undefined = useMemo((): CategoryData[] | undefined => (
    lessons?.elements
      .map((thumbnailList: ThumbnailList): CategoryData => {
        const filteredItems: Thumbnail[] = filterThumbnails(thumbnailList.items);

        return {
          title: thumbnailList.title,
          cardData: filteredItems.map(transformThumbnailIntoCardData),
          tableData: filteredItems.map(transformThumbnailIntoTableData),
          listLength: filteredItems.length,
        };
      })
      .filter((element: CategoryData): boolean => !!element.listLength)
  ), [lessons, filterThumbnails, transformThumbnailIntoCardData, transformThumbnailIntoTableData]);

  const closeCollapsibleSidePanel: () => void = (): void => {
    setIsSidePanelOpen(false);
    redirect({ route: RouteNameEnum.KnowledgeBase, replace: true });
  };

  return (
    <>
      <Container>
        {!!lessons && (
          <StyledContentFilters
            filters={[
              ...filters.map((item: ContentFilter): ContentFilter => (
                item.type === ContentFilterType.DatesRange ? {
                  ...item,
                  label: t(item.label),
                } : {
                  ...item,
                  label: t(item.label),
                  options: item.options.map((option: ContentFilterOption): ContentFilterOption => ({
                    ...option,
                    label: t(option.label),
                  })),
                }
              )),
              ...lessons.tags ? [{
                options: lessons.tags.map((tag: Tag): ContentFilterOption => ({ value: tag.name, label: tag.name })),
                name: FilterName.Tags,
                label: t('competence.app.knowledgeBaseV2.filters.tags'),
                type: ContentFilterType.Tag as const,
                isFullWidth: true,
              }] : [],
            ]}
            initialSelectedFilters={activeFilters}
            onFiltersChange={setActiveFilters}
            onSearchValueChange={setSearchValue}
            searchPlaceholder={t('competence.app.knowledgeBaseV2.searchPlaceholder')}
          >
            <ViewOptions />
          </StyledContentFilters>
        )}
        <MiddleContainer $isListViewMode={viewMode === KnowledgeBaseViewMode.List}>
          <CategorySection
            cardData={inProgressCardData}
            categoryTitle={t('competence.app.knowledgeBaseV2.inProgressSectionTitle')}
            isListMode={viewMode === KnowledgeBaseViewMode.List}
            isLoading={isFetching || isPending}
            listLength={filterThumbnails(inProgressMaterials).length}
            tableData={inProgressTableData}
            viewMode={viewMode}
            withVerticalAlignment
          />
        </MiddleContainer>
        <BottomContainer>
          {categoryData?.length ? (
            categoryData.map((category: CategoryData): JSX.Element | false => (
              <CategorySection
                cardData={category.cardData}
                categoryTitle={category.title}
                emptyGridColors={{ bgColor: Color.White }}
                isListMode={viewMode === KnowledgeBaseViewMode.List}
                isLoading={isFetching || isPending}
                key={category.title}
                listLength={category.listLength}
                tableData={category.tableData}
                viewMode={viewMode}
              />
            ))
          ) : (
            <StyledEmptyPlaceholder
              colorsData={{ bgColor: Color.White }}
              text={t('competence.app.knowledgeBaseV2.categorySection.emptyListMessage')}
            />
          )}
        </BottomContainer>
        <HelpBanner
          data={{
            type: HelpBannerType.Contact,
            bgImage: isSmallDesktop
              ? ViewImage.CommonHelpBannerBg
              : ViewImage.CommonHelpBannerMobileBg,
            text: t('competence.app.global.helpBanner.needHelp'),
            buttonData: {
              label: t('competence.app.global.helpBanner.help'),
              externalUrl: `mailto:${AppConfig.platformContactEmail}`,
            },
          }}
        />
      </Container>
      <CollapsibleSidePanel
        isOpen={isSidePanelOpen}
        onClose={closeCollapsibleSidePanel}
        title={itemToShowOnSidePanel
          ? t(
            `competence.app.knowledgeBaseV2.sidePanelTitle.${itemToShowOnSidePanel.type === ThumbnailType.Files ? 'files' : 'videos'}`,
            { title: itemToShowOnSidePanel.name }
          )
          : ''
        }
      >
        {itemToShowOnSidePanel?.items?.map((item: Thumbnail): JSX.Element => (
          item.type === ThumbnailType.File ? (
            <DownloadBoxWrapper key={item.id}>
              <Separator />
              <StyledDownloadBox
                image={getCardImage(item)}
                linkData={{
                  label: item.name,
                  action: (): Promise<AxiosResponse<void>> => downloadFile(item.id),
                }}
              />
            </DownloadBoxWrapper>
          ) : (
            <CollapsibleSidePanelCardWrapper key={item.id} onClick={(): void => handleOnCardClick(item)}>
              <Card
                category={item.category}
                image={getCardImage(item)}
                title={item.name}
                variant={CardVariant.Horizontal}
              />
            </CollapsibleSidePanelCardWrapper>
          )
        ))}
      </CollapsibleSidePanel>
    </>
  );
};

export default KnowledgeBaseV2;
